import { getFeatureLabel } from './feature-catalog';

// Arbre des options (features_v2) affiché par l'outil Clients, construit à
// partir du catalogue servi par l'API puis fusionné avec la valeur du client :
// - toute option connue du catalogue s'affiche, même absente du JSON du
//   client (décochée ou vide) ; elle n'y est écrite qu'une fois renseignée ;
// - une option sans effet (`unused`) ne s'affiche que si le client l'a ;
// - une clé du client inconnue du catalogue s'affiche à sa place dans le JSON,
//   sous un libellé déduit de son nom ;
// - une option peut s'afficher sous une autre que son parent dans le JSON
//   (`displayUnder`), quand elle en dépend.

export const GENERAL_SECTION = '__general__';

export const FEATURE_FILTER = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

export const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const getIn = (object, path) =>
  path.reduce((current, key) => current?.[key], object);

export const setIn = (object, path, value) => {
  if (path.length === 0) return value;

  const [key, ...rest] = path;
  const current = isPlainObject(object) ? object : {};

  return { ...current, [key]: setIn(current[key], rest, value) };
};

const unsetIn = (object, path) => {
  if (!isPlainObject(object) || !(path[0] in object)) return object;

  const [key, ...rest] = path;

  if (rest.length === 0) {
    return Object.fromEntries(
      Object.entries(object).filter(([candidate]) => candidate !== key)
    );
  }

  return { ...object, [key]: unsetIn(object[key], rest) };
};

/**
 * Retire une option, puis ses objets parents devenus vides s'ils n'existaient
 * pas dans `baseline` : cocher puis décocher une option absente rend le JSON
 * d'origine.
 */
export const unsetAndPrune = (object, path, baseline) => {
  let next = unsetIn(object, path);

  for (let depth = path.length - 1; depth > 0; depth -= 1) {
    const parentPath = path.slice(0, depth);
    const parent = getIn(next, parentPath);

    if (
      !isPlainObject(parent) ||
      Object.keys(parent).length > 0 ||
      getIn(baseline, parentPath) !== undefined
    ) {
      break;
    }

    next = unsetIn(next, parentPath);
  }

  return next;
};

const inferType = (value) => {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'text';
  if (Array.isArray(value))
    return value.every((item) => typeof item === 'string') ? 'list' : 'json';
  if (isPlainObject(value))
    return typeof value.enabled === 'boolean' ? 'toggle' : 'group';
  return 'json';
};

// Types du catalogue compatibles avec la valeur réellement en base : sinon la
// valeur l'emporte (le catalogue ne doit pas casser l'affichage d'un client).
const COMPATIBLE_TYPES = {
  boolean: ['boolean'],
  number: ['number'],
  text: ['text'],
  list: ['list'],
  json: ['list', 'json', 'group', 'toggle'],
  group: ['group', 'toggle'],
  toggle: ['group', 'toggle'],
};

const resolveType = (catalogType, value) => {
  if (value === undefined) return catalogType || 'json';

  const valueType = inferType(value);

  return catalogType && COMPATIBLE_TYPES[catalogType]?.includes(valueType)
    ? catalogType
    : valueType;
};

const CONTAINER_TYPES = ['group', 'toggle', 'boolean'];

export const isCheckable = (node) =>
  node.type === 'toggle' || node.type === 'boolean';

export const isChecked = (node) =>
  node.type === 'toggle' ? node.value?.enabled === true : node.value === true;

const hasSetting = (node) => {
  if (node.value === undefined || node.value === null) return false;
  if (node.type === 'text' || node.type === 'list' || node.type === 'json')
    return node.value.length === undefined || node.value.length > 0;
  return true;
};

/** Option cochée, ou réglage renseigné. */
export const isActive = (node) =>
  isCheckable(node)
    ? isChecked(node)
    : node.type !== 'group' && hasSetting(node);

export const hasCheckedInSubtree = (node) =>
  (isCheckable(node) && isChecked(node)) ||
  node.children.some(hasCheckedInSubtree);

/** Nombre d'options cochées (hors options sans effet). */
export const countChecked = (nodes) =>
  nodes.reduce(
    (sum, node) =>
      sum +
      (isCheckable(node) && isChecked(node) && !node.entry?.unused ? 1 : 0) +
      countChecked(node.children),
    0
  );

const compareNodes = (rank) => (a, b) =>
  rank(a.key) - rank(b.key) || a.key.localeCompare(b.key);

/**
 * Arbre d'affichage : sections (clés de premier niveau) contenant les nœuds
 * `{ key, path, entry, type, value, children }`.
 */
export const buildFeatureTree = (catalog, value) => {
  const features = catalog?.features || {};
  const catalogKeys = Object.keys(features);
  const ranks = new Map(catalogKeys.map((key, index) => [key, index]));
  const rank = (key) => ranks.get(key) ?? Number.MAX_SAFE_INTEGER;
  const nodes = new Map();

  const addNode = (key, entry) => {
    const path = key.split('.');
    const nodeValue = getIn(value, path);

    nodes.set(key, {
      key,
      path,
      entry,
      type: resolveType(entry?.type, nodeValue),
      value: nodeValue,
      children: [],
    });
  };

  catalogKeys.forEach((key) => {
    const entry = features[key];
    if (entry.unused && getIn(value, key.split('.')) === undefined) return;
    addNode(key, entry);
  });

  const addUnknownKeys = (object, parentPath) => {
    Object.entries(object).forEach(([key, child]) => {
      const path = [...parentPath, key];
      const nodeKey = path.join('.');

      if (!nodes.has(nodeKey)) addNode(nodeKey, null);

      const node = nodes.get(nodeKey);
      if (
        isPlainObject(child) &&
        (node.type === 'group' || node.type === 'toggle')
      )
        addUnknownKeys(child, path);
    });
  };

  if (isPlainObject(value)) addUnknownKeys(value, []);

  // Parent d'affichage : `displayUnder`, sinon le plus proche ancêtre dans le
  // JSON. Le `enabled` d'un objet `toggle` est la case de l'objet, et ce qui
  // vit dans une valeur JSON / liste en fait partie : pas de nœud propre.
  const findParent = (node) => {
    const displayParent = nodes.get(node.entry?.displayUnder);
    if (displayParent) return displayParent;

    for (let depth = node.path.length - 1; depth > 0; depth -= 1) {
      const ancestor = nodes.get(node.path.slice(0, depth).join('.'));
      if (ancestor) return ancestor;
    }

    return null;
  };

  const roots = [];

  nodes.forEach((node) => {
    const jsonParent = nodes.get(node.path.slice(0, -1).join('.'));
    const lastKey = node.path[node.path.length - 1];
    if (lastKey === 'enabled' && jsonParent?.type === 'toggle') return;

    const parent = findParent(node);

    if (!parent) roots.push(node);
    else if (CONTAINER_TYPES.includes(parent.type)) parent.children.push(node);
  });

  nodes.forEach((node) => node.children.sort(compareNodes(rank)));
  roots.sort(compareNodes(rank));

  // Les clés de premier niveau qui ne sont pas des objets sont regroupées
  // dans une section « Général ».
  const isGeneral = (node) =>
    !isPlainObject(node.value) &&
    node.type !== 'group' &&
    node.type !== 'toggle';
  const general = roots.filter(isGeneral);
  const objects = roots.filter((node) => !isGeneral(node));

  return [
    ...(general.length > 0
      ? [{ key: GENERAL_SECTION, title: 'Général', nodes: general }]
      : []),
    ...objects.map((node) =>
      node.type === 'group'
        ? {
            key: node.key,
            title: getFeatureLabel(catalog, node.path),
            description: node.entry?.description,
            nodes: node.children,
          }
        : {
            key: node.key,
            title: getFeatureLabel(catalog, node.path),
            nodes: [node],
          }
    ),
  ];
};

const normalize = (text) =>
  (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const normalizeSearch = (search) => normalize(search.trim());

/**
 * Une option correspond à la recherche si son libellé, son explication ou son
 * chemin technique contient le texte cherché.
 */
const isSelfMatching = (catalog, node, query) =>
  [
    getFeatureLabel(catalog, node.path),
    node.entry?.description,
    node.entry?.warning,
    node.key,
  ]
    .map(normalize)
    .some((text) => text.includes(query));

const matchesFilter = (node, filter) => {
  if (filter === FEATURE_FILTER.ACTIVE) return isActive(node);
  if (filter === FEATURE_FILTER.INACTIVE) return !isActive(node);
  return true;
};

/**
 * Sous-options affichées sans recherche : toutes sous une option cochée (ou
 * un groupe) ; sous une option décochée, seulement celles qui restent cochées
 * (incohérence à montrer, elles sont sans effet).
 */
const getStructuralChildren = (node) =>
  isCheckable(node) && !isChecked(node)
    ? node.children.filter(hasCheckedInSubtree)
    : node.children;

/**
 * Nœuds à afficher pour une recherche (`query`, normalisée) et un filtre
 * (activées / désactivées) : chaque nœud gardé porte `visibleChildren`. Une
 * option qui correspond elle-même à la recherche s'affiche avec ses
 * sous-options ; sinon seules ses sous-options correspondantes restent. Un
 * groupe ne s'affiche que s'il garde au moins une option.
 */
export const filterFeatureNodes = (catalog, nodes, { query = '', filter }) =>
  nodes
    .map((node) => {
      const isQueryMatch = !query || isSelfMatching(catalog, node, query);
      const children = filterFeatureNodes(
        catalog,
        isQueryMatch ? getStructuralChildren(node) : node.children,
        { query: isQueryMatch ? '' : query, filter }
      );
      const isShown =
        node.type !== 'group' && isQueryMatch && matchesFilter(node, filter);

      return isShown || children.length > 0
        ? { ...node, visibleChildren: children }
        : null;
    })
    .filter(Boolean);
